import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { observer, inject } from 'mobx-react';
import { observable, action, toJS } from 'mobx';
import { findInArray } from 'utils/helpers';
import { FormattedMessage } from 'ui/i18n';
import { getIndexForItem } from 'state/helpers';
import { COUNTRIES, COUNTRY_SHORT_NAMES } from 'config/constants';

import {
	Dropdown,
	ErrorMessage,
	Loader,
	DropshipContactMessage,
} from 'ui/components';

import './AddressModal.scss';

const FIELDS = [
	'name',
	'technician',
	'address1',
	'address2',
	'city',
	'zip',
	'phone',
	'email',
	'state',
	'country',
	'stateAbbreviation',
];

@inject('AppState', 'CartState')
@observer
export default class AddressModal extends Component {
	static propTypes = {
		AppState: PropTypes.object.isRequired,
		CartState: PropTypes.object.isRequired,
		show: PropTypes.bool.isRequired,
		onRequestClose: PropTypes.func.isRequired,
		inEditMode: PropTypes.bool.isRequired,
		item: PropTypes.object.isRequired,
		cacheNewAddress: PropTypes.object,
		saveCacheAddress: PropTypes.func.isRequired,
		clearCacheNewAddress: PropTypes.func.isRequired,
	};

	static defaultProps = { cacheNewAddress: null };

	componentWillMount() {
		this.props.CartState.getCountriesAndStates().then(() => {
			this.setCountryAndStates(
				this.props.CartState.getCountriesAndStates.state.result
			);

			this.init(this.props.cacheNewAddress);
		});
	}

	componentWillUnMount() {
		this.resetState();
	}

	@observable
	address = {
		name: '',
		technician: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		stateAbbreviation: '',
		country: '',
		zip: '',
		phone: '',
		email: '',
	};
	@observable currentCountry = '';
	@observable currentState = '';
	@observable currentStates = [];
	@observable
	inEditMode = this.props.inEditMode && !!this.props.item.drop_ship_address;
	@observable isDuplicate = false;
	@observable currentName = '';

	@observable
	errorsIds = {
		name: '',
		technician: '',
		address1: '',
		address2: '',
		city: '',
		zip: '',
		phone: '',
		email: '',
	};

	@observable
	invalidFields = {
		name: false,
		technician: false,
		address1: false,
		address2: false,
		city: false,
		zip: false,
		phone: false,
		email: false,
	};

	countryNames = {
		USA: 'US',
		US: 'USA',
		PR: 'USA', // treat PR users as USA for Drop Ship Address
		CA: 'Canada',
		GB: 'United Kingdom',
		IE: 'Ireland',
		AU: 'Australia',
		NZ: 'New Zealand',
	};

	get isUSASelected() {
		return (
			this.address.country === this.countryNames.US ||
			this.address.country === this.countryNames.USA
		);
	}

	get isCanadaSelected() {
		return this.address.country === this.countryNames.CA;
	}

	get isAustraliaSelected() {
		return this.address.country === this.countryNames.AU;
	}

	getPhonePattern() {
		return this.isAustraliaSelected
			? /^[0-9]{10}$/
			: /^[0-9]\d{2}-\d{3}-\d{4}$/;
	}

	getMaxPhoneLength() {
		const MAX_PHONE_LENGTH = 32;
		const MAX_PHONE_LENGTH_AUS = 10;

		return this.isAustraliaSelected ? MAX_PHONE_LENGTH_AUS : MAX_PHONE_LENGTH;
	}

	isValid(initialItem) {
		this.checkAddressExists(initialItem);

		for (const prop in this.invalidFields) {
			if (this.invalidFields[prop]) {
				return false;
			}
		}

		return true;
	}

	@action
	addNewAddress() {
		this.validate();
		const indexOfInitialItem = getIndexForItem(
			this.props.CartState.cartItems,
			this.props.item,
			this.props.item.is_pack
		);
		const initialItem = this.props.CartState.cartItems[indexOfInitialItem];

		const item = {
			drop_ship_address: this.address,
			...initialItem,
		};

		if (this.isValid(initialItem) && !this.isDuplicate) {
			const isAddOrUpdate =
				this.inEditMode &&
				initialItem.drop_ship_address.name === this.address.name;

			if (this.currentCountry.value === COUNTRIES.US) {
				this.address.country = COUNTRY_SHORT_NAMES.US;

				if (
					!this.inEditMode ||
					(this.inEditMode &&
						!this.isEquivalent(initialItem.drop_ship_address, this.address))
				) {
					this.props.CartState.setValidatedItem(this.props.item);
					this.props.CartState.validateDropShipAddress(this.address);
					this.props.saveCacheAddress(toJS(this.address));
				} else {
					this.callDropshipEndPoints(isAddOrUpdate, item);
				}
			} else {
				this.callDropshipEndPoints(isAddOrUpdate, item);
			}
			this.closeModal();
		}
	}

	callDropshipEndPoints(isAddOrUpdate, item) {
		if (isAddOrUpdate) {
			this.props.CartState.updateDropShipAddress(this.address, item);
		} else {
			this.props.CartState.addDropShipAddress(this.address, item);
		}
	}

	isEquivalent(itemDropship, currentDropship) {
		return (
			itemDropship.address1 === currentDropship.address1 &&
			itemDropship.address2 === currentDropship.address2 &&
			itemDropship.country === currentDropship.country &&
			itemDropship.state === currentDropship.state &&
			itemDropship.city === currentDropship.city &&
			itemDropship.zip === currentDropship.zip
		);
	}

	@action
	checkAddressExists(initialItem) {
		if (
			!this.inEditMode ||
			(this.inEditMode &&
				initialItem.drop_ship_address.name !== this.address.name)
		) {
			const existingDropShip = this.props.CartState.cart.drop_ships.filter(
				dropship => dropship.name === this.address.name
			);

			if (existingDropShip.length > 0) {
				this.isDuplicate = true;
				this.currentName = this.address.name;
			} else {
				this.isDuplicate = false;
			}
		} else {
			this.isDuplicate = false;
		}
	}

	handleOnRequestClose() {
		this.closeModal();
		this.props.clearCacheNewAddress();
	}

	closeModal() {
		this.resetState();

		this.props.onRequestClose();
	}

	getStateAbbreviation(stateName) {
		return findInArray(this.currentCountry.states, stateName, 'value')
			.abbreviation;
	}

	@action
	init(cachedAddress) {
		if (this.inEditMode) {
			const editedAddress = cachedAddress || this.props.item.drop_ship_address;

			FIELDS.forEach(field => {
				this.address[field] = editedAddress[field];
			});

			const dropshipCountry =
				editedAddress.country === COUNTRY_SHORT_NAMES.US
					? COUNTRIES.US
					: editedAddress.country;

			this.currentCountry = findInArray(
				this.props.CartState.getCountriesAndStates.state.result,
				dropshipCountry,
				'value'
			);

			this.currentStates = this.currentCountry.states;
			this.currentState = editedAddress.state;
		} else if (cachedAddress === null) {
			this.address = {
				name: '',
				technician: '',
				address1: '',
				address2: '',
				city: '',
				state: '',
				stateAbbreviation: '',
				country: '',
				zip: '',
				phone: '',
				email: '',
			};

			this.setCountryAndStates(
				this.props.CartState.getCountriesAndStates.state.result
			);
		} else {
			this.setCountryAndStateWithAddressCashed(cachedAddress);
		}
	}

	setCountryAndStateWithAddressCashed(cachedAddress) {
		this.address = cachedAddress;
		this.currentState = this.address.state;

		this.allCountries = this.props.CartState.getCountriesAndStates.state.result;
		this.dropdownCountry = this.isUSASelected
			? this.countryNames.US
			: this.address.country;

		this.currentCountry = findInArray(
			this.allCountries,
			this.dropdownCountry,
			'value'
		);
		this.currentStates = this.currentCountry.states;
	}

	@action
	setCountryAndStates(countries) {
		const userCountry = this.countryNames[
			this.props.AppState.user.country.short_name
		];
		this.currentCountry = findInArray(countries, userCountry, 'value');
		this.currentStates = this.currentCountry.states;

		if (this.currentCountry.states.length > 0) {
			this.currentState = this.currentCountry.states[0].value;
			this.address.state = this.currentState;
			this.address.stateAbbreviation = this.currentCountry.states[0].abbreviation;
		}

		this.address.country = this.currentCountry.value;
	}

	@action
	resetState() {
		this.invalidFields = {
			name: false,
			technician: false,
			address1: false,
			address2: false,
			city: false,
			zip: false,
			phone: false,
			email: false,
		};
	}

	@action
	onChangeHandler(fieldName, e) {
		this.address[fieldName] = e.target.value;
	}

	@action
	validateAddress2(pattern, max_length) {
		if (pattern.test(this.address.address2)) {
			this.invalidFields.address2 = false;
			this.errorsIds.address2 = '';
		} else {
			this.invalidFields.address2 = true;
			this.errorsIds.address2 = 'cart.newAddress.InvalidAddress2';
		}

		if (!this.invalidFields.address2) {
			if (this.address.address2.length <= max_length) {
				this.invalidFields.address2 = false;
			} else {
				this.invalidFields.address2 = true;
				this.errorsIds.address2 = 'cart.newAddress.GreaterThanAddress2';
			}
		}
	}

	@action
	validatePhone(pattern, max_length) {
		if (this.address.phone) {
			if (
				this.isUSASelected ||
				this.isCanadaSelected ||
				this.isAustraliaSelected
			) {
				if (pattern.test(this.address.phone)) {
					this.invalidFields.phone = false;
					this.errorsIds.phone = '';
				} else {
					this.invalidFields.phone = true;
					this.errorsIds.phone = this.isAustraliaSelected
						? 'cart.newAddress.InvalidPhoneAUS'
						: 'cart.newAddress.InvalidPhone';
				}
			} else {
				this.invalidFields.phone = false;
				this.errorsIds.phone = '';
			}

			if (!this.invalidFields.phone) {
				if (this.address.phone.length <= max_length) {
					this.invalidFields.phone = false;
				} else {
					this.invalidFields.phone = true;
					this.errorsIds.phone = 'cart.newAddress.GreaterThanPhone';
				}
			}
		} else {
			this.invalidFields.phone = true;
			this.errorsIds.phone = 'cart.newAddress.EmptyPhone';
		}
	}

	@action
	validateField(fieldName, pattern, max_length, errorsIds) {
		if (this.address[fieldName]) {
			if (pattern.test(this.address[fieldName])) {
				this.invalidFields[fieldName] = false;
				this.errorsIds[fieldName] = '';
			} else {
				this.invalidFields[fieldName] = true;
				this.errorsIds[fieldName] = errorsIds.pattern;
			}

			if (!this.invalidFields[fieldName]) {
				if (this.address[fieldName].length <= max_length) {
					this.invalidFields[fieldName] = false;
				} else {
					this.invalidFields[fieldName] = true;
					this.errorsIds[fieldName] = errorsIds.greaterThan;
				}
			}
		} else {
			this.invalidFields[fieldName] = true;
			this.errorsIds[fieldName] = errorsIds.empty;
		}
	}

	@action
	validate() {
		const PATTERNS = {
			name: /^[\w-_().,'/‘’\s]+$/,
			technician: /^[\w-_().,'/‘’\s]+$/,
			city: /^[a-zA-Z0-9-.,'‘’\s]+$/,
			address1: /^[\w-_().,'/‘’\s]+$/,
			address2: /^[\w-_().,'/‘’\s]*$/,
			zip: /^[a-zA-Z0-9\s-]+$/,
			phone: this.getPhonePattern(),
			email: /^(?!\.)([a-zA-Z0-9_&+-]|\.(?![.@]))+@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9&+-]+\.)+))([a-zA-Z&+-]{2,4}|[0-9]{1,3})(\]?)$/,
		};

		const MAX_LENGTHS = {
			name: 35,
			technician: 30,
			address1: 100,
			address2: 100,
			city: 30,
			zip: 10,
			phone: this.getMaxPhoneLength(),
			email: 52,
		};

		const ERROR_IDS = {
			name: {
				pattern: 'cart.newAddress.InvalidName',
				greaterThan: 'cart.newAddress.GreaterThanName',
				empty: 'cart.newAddress.EmptyName',
			},
			technician: {
				pattern: 'cart.newAddress.InvalidTechnician',
				greaterThan: 'cart.newAddress.GreaterThanTechnician',
				empty: 'cart.newAddress.EmptyTechnician',
			},
			address1: {
				pattern: 'cart.newAddress.InvalidAddress1',
				greaterThan: 'cart.newAddress.GreaterThanAddress1',
				empty: 'cart.newAddress.EmptyAddress1',
			},
			city: {
				pattern: 'cart.newAddress.InvalidCity',
				greaterThan: 'cart.newAddress.GreaterThanCity',
				empty: 'cart.newAddress.EmptyCity',
			},
			zip: {
				pattern: 'cart.newAddress.InvalidZip',
				greaterThan: 'cart.newAddress.GreaterThanZip',
				empty: 'cart.newAddress.EmptyZip',
			},
			email: {
				pattern: 'cart.newAddress.InvalidEmail',
				greaterThan: 'cart.newAddress.GreaterThanEmail',
				empty: 'cart.newAddress.EmptyEmail',
			},
		};

		for (const prop in this.address) {
			switch (prop) {
				case 'address2':
					this.validateAddress2(PATTERNS[prop], MAX_LENGTHS[prop]);
					break;

				case 'phone':
					this.validatePhone(PATTERNS[prop], MAX_LENGTHS[prop]);
					break;
				case 'state':
					break;
				case 'country':
					break;
				case 'stateAbbreviation':
					break;

				default:
					this.validateField(
						prop,
						PATTERNS[prop],
						MAX_LENGTHS[prop],
						ERROR_IDS[prop]
					);
			}
		}
	}

	@action
	handleCountryChange(value) {
		this.currentCountry = findInArray(
			this.props.CartState.getCountriesAndStates.state.result,
			value,
			'value'
		);
		this.currentStates = this.currentCountry.states;
		this.currentState = this.currentStates.length
			? this.currentStates[0].value
			: '';

		this.address.state = this.currentState;
		this.address.stateAbbreviation = this.currentStates.length
			? this.currentStates[0].abbreviation
			: '';
		this.address.country = this.currentCountry.value;
	}

	@action
	handleStateChange(value) {
		this.currentState = value;
		this.address.state = value;
		this.address.stateAbbreviation = this.getStateAbbreviation(value);
	}

	render() {
		const { show } = this.props;
		const { getCountriesAndStates } = this.props.CartState;
		return (
			<Modal
				className="ReactModal__Content NewAddress"
				id="NewAddressModal"
				overlayClassName="ReactModal__Overlay"
				isOpen={show}
				onRequestClose={this.handleOnRequestClose.bind(this)}
				contentLabel="New address"
			>
				{getCountriesAndStates.handle({
					loading: () => <Loader />,
					resolved: () =>
						<div>
							<div className="NewAddress-header">
								<h4>
									{this.inEditMode
										? <FormattedMessage id="dropship.editDropshipTitle" />
										: <FormattedMessage id="dropShip.newDropshipTitle" />}
								</h4>
							</div>
							<form className="NewAddress-form">
								<div className="form-group inline">
									<label>
										<FormattedMessage id="dropship.nameLabel" />
									</label>
									<span className="required">*</span>
									<input
										type="text"
										value={this.address.name}
										id="NewAddress-form-input-name"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'name')}
									/>
									{this.invalidFields.name &&
										<ErrorMessage
											show={this.invalidFields.name}
											messageId={this.errorsIds.name}
										/>}

									{this.isDuplicate &&
										<ErrorMessage
											show={this.isDuplicate}
											messageId="cart.newAddress.DuplicateDropship"
											values={{ dropshipName: this.currentName }}
										/>}
								</div>

								<div className="form-group inline">
									<label>
										<FormattedMessage id="dropship.technicianLabel" />
									</label>
									<span className="required">*</span>
									<input
										type="text"
										value={this.address.technician}
										id="NewAddress-form-input-tech"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'technician')}
									/>

									{this.invalidFields.technician &&
										<ErrorMessage
											show={this.invalidFields.technician}
											messageId={this.errorsIds.technician}
										/>}
								</div>

								<div className="form-group NewAddress-form-control__fullWidth">
									<label>
										<FormattedMessage id="dropship.address1Label" />
									</label>
									<span className="required">*</span>
									<input
										type="text"
										value={this.address.address1}
										id="NewAddress-form-input-address1"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'address1')}
									/>

									{this.invalidFields.address1 &&
										<ErrorMessage
											show={this.invalidFields.address1}
											messageId={this.errorsIds.address1}
										/>}
								</div>

								<div className="form-group NewAddress-form-control__fullWidth">
									<label>
										<FormattedMessage id="dropship.address2Label" />
									</label>
									<input
										type="text"
										value={this.address.address2}
										id="NewAddress-form-input-address2"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'address2')}
									/>

									{this.invalidFields.address2 &&
										<ErrorMessage
											show={this.invalidFields.address2}
											messageId={this.errorsIds.address2}
										/>}
								</div>

								<div>
									{this.currentCountry &&
										<div
											className="form-group inline"
											id="NewAddress-form-dropdown-country"
										>
											<label>
												<FormattedMessage id="dropship.countryLabel" />
											</label>
											<span className="required">*</span>
											<Dropdown
												title={this.currentCountry.value}
												items={
													this.props.CartState.getCountriesAndStates.state
														.result
												}
												onSelect={this.handleCountryChange.bind(this)}
											/>
										</div>}
									{!!this.currentStates.length &&
										<div
											className={
												this.currentStates.length ? 'form-group inline' : 'hide'
											}
											id="NewAddress-form-dropdown-state"
										>
											<label>
												<FormattedMessage id="dropship.stateLabel" />
											</label>
											<span className="required">*</span>
											<Dropdown
												title={this.currentState}
												items={this.currentStates}
												onSelect={this.handleStateChange.bind(this)}
											/>
										</div>}

									<div className="form-group inline NewAddress-form-control__smWidth">
										<label>
											<FormattedMessage id="dropship.cityLabel" />
										</label>
										<span className="required">*</span>
										<input
											type="text"
											value={this.address.city}
											id="NewAddress-form-input-city"
											className="form-control"
											onChange={this.onChangeHandler.bind(this, 'city')}
										/>

										{this.invalidFields.city &&
											<ErrorMessage
												show={this.invalidFields.city}
												messageId={this.errorsIds.city}
											/>}
									</div>
								</div>

								<div className="form-group inline">
									<label>
										<FormattedMessage id="dropship.zipLabel" />
									</label>
									<span className="required">*</span>
									<input
										type="text"
										value={this.address.zip}
										id="NewAddress-form-input-zip"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'zip')}
									/>

									{this.invalidFields.zip &&
										<ErrorMessage
											show={this.invalidFields.zip}
											messageId={this.errorsIds.zip}
										/>}
								</div>

								<div className="form-group inline">
									<label>
										<FormattedMessage id="dropship.phoneLabel" />
									</label>
									<span className="required">*</span>
									<input
										type="text"
										value={this.address.phone}
										id="NewAddress-form-input-phone"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'phone')}
									/>

									{this.invalidFields.phone &&
										<ErrorMessage
											show={this.invalidFields.phone}
											messageId={this.errorsIds.phone}
											values={{
												max_length: this.getMaxPhoneLength().toString(),
											}}
										/>}
								</div>

								<div className="form-group NewAddress-form-control__fullWidth">
									<label>
										<FormattedMessage id="dropship.emailLabel" />
									</label>
									<span className="required">*</span>
									<input
										type="text"
										value={this.address.email}
										id="NewAddress-form-input-email"
										className="form-control"
										onChange={this.onChangeHandler.bind(this, 'email')}
									/>
									{this.invalidFields.email &&
										<ErrorMessage
											show={this.invalidFields.email}
											messageId={this.errorsIds.email}
										/>}
									{this.invalidFields.email &&
										<DropshipContactMessage
											show={this.invalidFields.email}
											country={this.address.country}
										/>}
								</div>
							</form>
							<div className="row NewAddress-footer">
								<div className="pull-right">
									<button
										className="btn btn-primary"
										id="NewAddress-footer-button-primary"
										onClick={this.addNewAddress.bind(this)}
									>
										OK
									</button>
									<button
										className="btn btn-secondary"
										id="NewAddress-footer-button-secondary"
										onClick={this.handleOnRequestClose.bind(this)}
									>
										<FormattedMessage id="general.cancelText" />
									</button>
								</div>
							</div>
						</div>,
				})}
			</Modal>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/components/AddressModal/index.js